import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, Phone, ArrowRight, Mail, FileText } from "lucide-react";

const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const guideType = searchParams.get('guide') || 'default';
  const firstName = searchParams.get('name') || 'there';
  const email = searchParams.get('email') || '';

  useEffect(() => {
    document.title = "Thank You! | Digital Media Ninja";
  }, []);

  const getGuideTitle = () => {
    if (guideType === 'gym-marketing') return '2026 Gym Marketing Playbook';
    if (guideType === 'video-marketing') return '2026 DFW Video Marketing Guide';
    if (guideType === 'ai-receptionist') return "2026 AI Receptionist Buyer's Guide";
    return 'Your Digital Marketing Resource';
  };

  const getGuideDescription = () => {
    if (guideType === 'gym-marketing') return 'Your comprehensive guide to dominating gym marketing in Dallas-Fort Worth.';
    if (guideType === 'video-marketing') return 'Everything you need to create video content that converts for your Dallas business.';
    if (guideType === 'ai-receptionist') return 'The complete guide to implementing AI phone answering for your service business.';
    return 'Thank you for downloading. Check your email for additional resources.';
  };

  const getRelatedPage = () => {
    if (guideType === 'gym-marketing') return '/gym-marketing';
    if (guideType === 'video-marketing') return '/video-marketing';
    if (guideType === 'ai-receptionist') return '/ai-receptionist';
    return '/services';
  };

  const getRelatedPageName = () => {
    if (guideType === 'gym-marketing') return 'Gym Marketing Services';
    if (guideType === 'video-marketing') return 'Video Marketing Services';
    if (guideType === 'ai-receptionist') return 'AI Receptionist Services';
    return 'Our Services';
  };

  return (
    <div className="min-h-screen bg-black" data-testid="thank-you-page">
      <Helmet>
        <title>Thank You! Your Guide is Ready | Digital Media Ninja</title>
        <meta name="description" content="Thank you for downloading your guide from Digital Media Ninja." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="pt-32 pb-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 rounded-full bg-[#00FF85]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#00FF85]" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Thank You, {firstName}!
              </h1>
              <p className="text-xl text-gray-300">
                Your download is on its way
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#111] to-[#1a1a2e] rounded-2xl border border-white/10 p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#00FF85]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-7 h-7 text-[#00FF85]" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">{getGuideTitle()}</h2>
                  <p className="text-gray-400">{getGuideDescription()}</p>
                </div>
              </div>

              {email && (
                <div className="flex items-center gap-3 p-4 bg-[#00FF85]/10 rounded-xl border border-[#00FF85]/20 mb-6">
                  <Mail className="w-5 h-5 text-[#00FF85]" />
                  <p className="text-gray-300">
                    Your guide has been sent to <strong className="text-white">{email}</strong> for your records
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Recommended Next Steps:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF85]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#00FF85]">1</span>
                    </div>
                    <span className="text-gray-300">Review the guide thoroughly and identify quick wins</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF85]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#00FF85]">2</span>
                    </div>
                    <span className="text-gray-300">Implement 2-3 strategies this week</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FF85]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#00FF85]">3</span>
                    </div>
                    <span className="text-gray-300">Schedule a strategy call to discuss your goals</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-[#111] rounded-xl border border-white/10 p-6 hover:border-[#00FF85]/30 transition-colors">
                <Calendar className="w-8 h-8 text-[#00FF85] mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Schedule a Strategy Call</h3>
                <p className="text-gray-400 mb-4 text-sm">
                  Get personalized recommendations for your business from our Dallas team.
                </p>
                <Link to="/contact">
                  <Button className="w-full bg-[#00FF85] hover:bg-[#00FF85]/90 text-black font-semibold">
                    Book Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="bg-[#111] rounded-xl border border-white/10 p-6 hover:border-[#00FF85]/30 transition-colors">
                <Phone className="w-8 h-8 text-[#00FF85] mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Have Questions?</h3>
                <p className="text-gray-400 mb-4 text-sm">
                  Our team is ready to help you implement these strategies.
                </p>
                <a href="tel:+14329349470">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                    Call (432) 934-9470
                  </Button>
                </a>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-400 mb-4">Continue exploring:</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to={getRelatedPage()}>
                  <Button variant="link" className="text-[#00FF85] hover:text-[#00FF85]/80">
                    {getRelatedPageName()} →
                  </Button>
                </Link>
                <Link to="/blog">
                  <Button variant="link" className="text-gray-400 hover:text-white">
                    Read Our Blog →
                  </Button>
                </Link>
                <Link to="/case-studies">
                  <Button variant="link" className="text-gray-400 hover:text-white">
                    View Case Studies →
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
